// src/components/Hero.tsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, Image, Brain, Target, ShieldCheck, Search, FileCode, Maximize2, X } from 'lucide-react';
import confusionMatrix from '../assets/confusion_matrix.png';
import './Hero.css';

const Hero: React.FC = () => {
  const [isFullscreen, setIsFullscreen] = useState(false);

  const openFullscreen = () => {
    setIsFullscreen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeFullscreen = () => {
    setIsFullscreen(false);
    document.body.style.overflow = 'unset';
  };

  return (
    <>
      <section className="hero">
        <div className="hero-container">
          <motion.div
            className="hero-left"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="hero-title">
              AI-Powered Malware<br />Detection
            </h1>
            <p className="hero-subtitle">
              Upload any binary file. Get a family classification, VirusTotal cross-validation, and a deployable YARA rule in seconds.
            </p>

            <div className="hero-stats">
              <span className="stat-pill">98.55% validation accuracy</span>
              <span className="stat-pill">25 malware families</span>
              <span className="stat-pill">75 AV engines cross-checked</span>
            </div>

            <p className="hero-description">
              Traditional antivirus depends on known signatures and fails against new or modified
              variants. This system takes a different approach; malware executables are converted
              into grayscale images by mapping raw byte values to pixel intensities. Different
              malware families produce visually distinct patterns, which a 4-block Convolutional
              Neural Network learns to recognize. Each block extracts progressively deeper
              features: from low-level byte textures in Block 1, to obfuscation artifacts and
              family-level structural similarities in Blocks 3 and 4. The result is a compressed
              internal representation the classifier uses to assign a malware family and confidence
              score, then cross-referenced against VirusTotal and converted into a YARA rule
              ready for deployment in SIEM or EDR tooling.
            </p>

            <div className="hero-features">
              <div className="feature-item">
                <Brain size={16} className="feature-icon" />
                <span>4-block CNN trained on the Malimg dataset (9,339 samples)</span>
              </div>
              <div className="feature-item">
                <ShieldCheck size={16} className="feature-icon" />
                <span>VirusTotal cross-validation — verdict backed by 75 AV engines</span>
              </div>
              <div className="feature-item">
                <FileCode size={16} className="feature-icon" />
                <span>Auto-generated YARA rules extracted from byte signatures</span>
              </div>
              <div className="feature-item">
                <Search size={16} className="feature-icon" />
                <span>Entropy analysis flags packed or encrypted samples</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="hero-right"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Pipeline diagram */}
            <div className="pipeline-diagram">
              <div className="pipeline-step">
                <FileText size={28} className="step-icon" />
                <span className="step-label">Binary file</span>
                <span className="step-sublabel">Any .exe, .dll, .bin</span>
              </div>
              <div className="pipeline-arrow">→</div>
              <div className="pipeline-step">
                <Image size={28} className="step-icon" />
                <span className="step-label">Grayscale image</span>
                <span className="step-sublabel">Bytes → pixels</span>
              </div>
              <div className="pipeline-arrow">→</div>
              <div className="pipeline-step">
                <Brain size={28} className="step-icon" />
                <span className="step-label">4-block CNN</span>
                <span className="step-sublabel">Feature extraction</span>
              </div>
              <div className="pipeline-arrow">→</div>
              <div className="pipeline-step">
                <Target size={28} className="step-icon" />
                <span className="step-label">Verdict</span>
                <span className="step-sublabel">Family + confidence</span>
              </div>
            </div>

            {/* CNN block breakdown */}
            <div className="cnn-blocks">
              <div className="cnn-block">
                <span className="block-number">1</span>
                <div className="block-info">
                  <span className="block-title">Basic pattern detection</span>
                  <span className="block-desc">Edges, textures, binary density</span>
                </div>
              </div>
              <div className="cnn-block">
                <span className="block-number">2</span>
                <div className="block-info">
                  <span className="block-title">Intermediate structure</span>
                  <span className="block-desc">Byte sequences, opcode distribution</span>
                </div>
              </div>
              <div className="cnn-block">
                <span className="block-number">3</span>
                <div className="block-info">
                  <span className="block-title">Behavioural features</span>
                  <span className="block-desc">Obfuscation artifacts, family similarities</span>
                </div>
              </div>
              <div className="cnn-block">
                <span className="block-number">4</span>
                <div className="block-info">
                  <span className="block-title">Threat representation</span>
                  <span className="block-desc">Abstract high-level classification</span>
                </div>
              </div>
            </div>

            {/* Confusion matrix with expand functionality */}
            <div className="hero-image">
              <p className="matrix-label">Validation confusion matrix — 98.55% accuracy across 25 families</p>
              <div className="image-container" onClick={openFullscreen}>
                <img
                  src={confusionMatrix}
                  alt="CNN confusion matrix showing classification accuracy across 25 malware families"
                  className="matrix-image"
                />
                <div className="image-overlay">
                  <Maximize2 size={24} className="expand-icon" />
                  <span>Click to expand</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Fullscreen Modal */}
      <AnimatePresence>
        {isFullscreen && (
          <motion.div
            className="fullscreen-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeFullscreen}
          >
            <motion.div
              className="fullscreen-content"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
            >
              <button className="close-fullscreen" onClick={closeFullscreen}>
                <X size={32} />
              </button>
              <img
                src={confusionMatrix}
                alt="CNN confusion matrix - full size"
                className="fullscreen-image"
              />
              <p className="fullscreen-caption">
                Validation confusion matrix — 98.55% accuracy across 25 malware families
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Hero;